import { Button } from '@eventbrite/marmalade';
import { defineMessages, useIntl } from 'react-intl';
import { CrownIcon } from '../_shared/CrownIcon';
import { CloudDecorations } from './CloudDecorations';
import styles from './SuperOrganizerAnnouncementModal.module.scss';

const messages = defineMessages({
  closeModal: {
    id: 'Close announcement',
    defaultMessage: 'Close announcement',
  },
  heading: {
    id: "You're a Top Organizer",
    defaultMessage: "You're a Top Organizer",
  },
  bodyParagraph1: {
    id: "Nice work — you're among the organizers driving strong attendance and community engagement on Eventbrite.",
    defaultMessage:
      "Nice work — you're among the organizers driving strong attendance and community engagement on Eventbrite.",
  },
  bodyParagraph2: {
    id: 'Your Top Organizer badge is now visible across your Organizer Page and events, helping attendees recognize the experiences you create.',
    defaultMessage:
      'Your Top Organizer badge is now visible across your Organizer Page and events, helping attendees recognize the experiences you create.',
  },
  verifiedBodyParagraph1: {
    id: 'Nice work — your verified badge has been upgraded to a Top Organizer badge.',
    defaultMessage: 'Nice work — your verified badge has been upgraded to a Top Organizer badge.',
  },
  verifiedBodyParagraph2: {
    id: "It's now visible across your Organizer Page and events, helping attendees recognize the great experiences you create.",
    defaultMessage:
      "It's now visible across your Organizer Page and events, helping attendees recognize the great experiences you create.",
  },
  bodyKeepGoing: {
    id: 'Keep it going.',
    defaultMessage: 'Keep it going.',
  },
  gotIt: {
    id: 'Got it',
    defaultMessage: 'Got it',
  },
  topOrganizer: {
    id: 'Top Organizer',
    defaultMessage: 'Top Organizer',
  },
});

interface Props {
  isShown: boolean;
  onClose: () => void;
  organizer: { name: string; avatarUrl: string; coverUrl?: string };
  isVerified: boolean;
}

export function SuperOrganizerAnnouncementModal({
  isShown,
  onClose,
  organizer,
  isVerified,
}: Props) {
  const intl = useIntl();

  if (!isShown) return null;

  const hasAvatar = organizer.avatarUrl.trim().length > 0;
  const hasCover = (organizer.coverUrl ?? '').trim().length > 0;
  const initial = organizer.name.charAt(0).toUpperCase();

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={intl.formatMessage(messages.closeModal)}
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 1.42871L6.42773 5L10 8.57227L8.57129 10.001L4.99902 6.42871L1.42871 10L0 8.57129L3.57031 5L0 1.42969L1.42871 0.000976562L4.99902 3.57129L8.57129 0L10 1.42871Z"
                fill="#3A3247"
              />
            </svg>
          </button>
        </div>

        <div className={styles.bodyContainer}>
          <div className={styles.leftSide}>
            <div className={styles.textContainer}>
              <div className={styles.headingContainer}>
                <h2 className={styles.heading}>{intl.formatMessage(messages.heading)}</h2>
                <span className={styles.crownBadge}>
                  <CrownIcon />
                </span>
              </div>
              <div className={styles.bodyText}>
                {isVerified ? (
                  <>
                    <p>{intl.formatMessage(messages.verifiedBodyParagraph1)}</p>
                    <p>{intl.formatMessage(messages.verifiedBodyParagraph2)}</p>
                  </>
                ) : (
                  <>
                    <p>{intl.formatMessage(messages.bodyParagraph1)}</p>
                    <p>{intl.formatMessage(messages.bodyParagraph2)}</p>
                  </>
                )}
                <p>{intl.formatMessage(messages.bodyKeepGoing)}</p>
              </div>
            </div>
            <div className={styles.actions}>
              <Button variant="primary" onClick={onClose}>
                {intl.formatMessage(messages.gotIt)}
              </Button>
            </div>
          </div>

          <div className={styles.rightSide}>
            <CloudDecorations />
            <div className={styles.gradientStrip} />
            <span className={styles.decorativeCrown}>
              <CrownIcon />
            </span>
            <div className={styles.previewCard}>
              <div className={styles.previewCover}>
                {(hasCover || hasAvatar) && (
                  <img
                    src={hasCover ? organizer.coverUrl : organizer.avatarUrl}
                    alt=""
                    className={hasCover ? undefined : styles.previewCoverBlur}
                  />
                )}
                <div className={styles.previewCoverOverlay} />
              </div>
              <div className={styles.previewInfo}>
                <div className={styles.previewAvatarWrap}>
                  <div className={styles.previewAvatar}>
                    {hasAvatar ? (
                      <img src={organizer.avatarUrl} alt="" />
                    ) : (
                      <span className={styles.previewAvatarInitial}>{initial}</span>
                    )}
                  </div>
                  <span className={styles.previewCrownBadge}>
                    <CrownIcon />
                  </span>
                </div>
                <div className={styles.previewText}>
                  <span className={styles.previewTopOrganizer}>
                    {intl.formatMessage(messages.topOrganizer)}
                  </span>
                  <span className={styles.previewName}>{organizer.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
